import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert, Row, Col, Badge } from 'react-bootstrap';
import { createLeaveRequest, calculateWorkingDaysAPI } from '../../services/api';

const EmployeeLeaveModal = ({ show, onHide, employee, onSuccess }) => {
    // **FORM STATE YÖNETİMİ**
    const [formData, setFormData] = useState({
        leaveType: 'yıllık',
        startDate: '',
        endDate: '',
        reason: '',
        approverName: ''
    });

    // **COMPONENT STATE'LERİ**
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [calculatedDays, setCalculatedDays] = useState(0);
    const [warningMessage, setWarningMessage] = useState('');

    const [workingDaysPreview, setWorkingDaysPreview] = useState(null);

    // **İZİN TİPLERİ**
    const leaveTypes = [
        { value: 'yıllık', label: 'Yıllık İzin' },
        { value: 'hastalık', label: 'Hastalık İzni' },
        { value: 'mazeret', label: 'Mazeret İzni' },
        { value: 'babalık', label: 'Babalık İzni' },
        { value: 'doğum', label: 'Doğum İzni' },
        { value: 'diğer', label: 'Diğer' }
    ];

    // **MODAL AÇILDIĞINDA FORM'U SIFIRLA**
    useEffect(() => {
        if (show) {
            setFormData({
                leaveType: 'yıllık',
                startDate: '',
                endDate: '',
                reason: '',
                approverName: ''
            });
            setCalculatedDays(0);
            setError('');
            setWarningMessage('');
        }
    }, [show]);

    // **TARİH DEĞİŞTİĞİNDE GÜN HESAPLA**
    // **WORKING DAYS PREVIEW CALCULATION**
    useEffect(() => {
        if (formData.startDate && formData.endDate) {
            const calculatePreview = async () => {
                try {
                    const response = await calculateWorkingDaysAPI(formData.startDate, formData.endDate);
                    if (response.success) {
                        setCalculatedDays(response.data.workingDays);
                        setWorkingDaysPreview(response.data);
                    }
                } catch (error) {
                    console.error('Working days preview error:', error);
                    // Fallback to simple calculation
                    const startDate = new Date(formData.startDate);
                    const endDate = new Date(formData.endDate);
                    if (endDate >= startDate) {
                        const diffTime = Math.abs(endDate - startDate);
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                        setCalculatedDays(diffDays);
                    }
                    setWorkingDaysPreview(null);
                }
            };

            calculatePreview();
        } else {
            setCalculatedDays(0);
            setWorkingDaysPreview(null);
        }
    }, [formData.startDate, formData.endDate]);

    // **FORM INPUT DEĞİŞİKLİKLERİNİ HANDLE ET**
    const handleFormChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // **İZİN TALEBİ OLUŞTUR**
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!employee?._id) {
            setError('Personel bilgisi bulunamadı');
            return;
        }

        if (calculatedDays <= 0) {
            setError('Geçerli tarih aralığı seçiniz');
            return;
        }

        if (!formData.approverName || formData.approverName.trim().length < 2) {
            setError('Onaylayacak kişi adı en az 2 karakter olmalıdır');
            return;
        }

        if (formData.leaveType === 'yıllık' && calculatedDays > (employee.leaveRights?.remaining || 0)) {
            setError('Yetersiz yıllık izin hakkı');
            return;
        }

        try {
            setLoading(true);
            setError('');

            const leaveData = {
                leaveType: formData.leaveType,
                startDate: formData.startDate,
                endDate: formData.endDate,
                reason: formData.reason || `${formData.leaveType} izni`,
                approverName: formData.approverName
            };

            const response = await createLeaveRequest(employee._id, leaveData);

            if (response.success) {
                onSuccess?.('İzin talebi başarıyla oluşturuldu');
                onHide();
            }
        } catch (error) {
            setError(error.response?.data?.message || 'İzin talebi oluşturulurken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    // **BUGÜNÜN TARİHİ (MIN DATE İÇİN)**
    const today = new Date().toISOString().split('T')[0];

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>📅 İzin Talebi Oluştur</Modal.Title>
            </Modal.Header>

            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {/* **PERSONEL BİLGİSİ** */}
                    {employee && (
                        <div className="mb-4 p-3 bg-light rounded">
                            <Row>
                                <Col md={6}>
                                    <h6 className="mb-1">{employee.personalInfo?.fullName}</h6>
                                    <small className="text-muted">
                                        {employee.workInfo?.position} - {employee.workInfo?.department}
                                    </small>
                                </Col>
                                <Col md={6} className="text-end">
                                    {employee.leaveRights && (
                                        <div>
                                            <Badge bg="primary">
                                                Yıllık İzin: {employee.leaveRights.remaining || 0} / {employee.leaveRights.annual || 0}
                                            </Badge>
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </div>
                    )}

                    {/* **HATA MESAJI** */}
                    {error && (
                        <Alert variant="danger">
                            {error}
                        </Alert>
                    )}

                    {/* **UYARI MESAJI** */}
                    {warningMessage && (
                        <Alert variant="warning">
                            {warningMessage}
                        </Alert>
                    )}

                    <Row>
                        <Col md={6}>
                            {/* **İZİN TÜRÜ** */}
                            <Form.Group className="mb-3">
                                <Form.Label>İzin Türü *</Form.Label>
                                <Form.Select
                                    required
                                    value={formData.leaveType}
                                    onChange={(e) => handleFormChange('leaveType', e.target.value)}
                                >
                                    {leaveTypes.map(type => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            {/* **BAŞLANGIÇ TARİHİ** */}
                            <Form.Group className="mb-3">
                                <Form.Label>Başlangıç Tarihi *</Form.Label>
                                <Form.Control
                                    type="date"
                                    required
                                    min={today}
                                    value={formData.startDate}
                                    onChange={(e) => handleFormChange('startDate', e.target.value)}
                                />
                            </Form.Group>

                            {/* **BİTİŞ TARİHİ** */}
                            <Form.Group className="mb-3">
                                <Form.Label>Bitiş Tarihi *</Form.Label>
                                <Form.Control
                                    type="date"
                                    required
                                    min={formData.startDate || today}
                                    value={formData.endDate}
                                    onChange={(e) => handleFormChange('endDate', e.target.value)}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            {/* **HESAPLANAN GÜN SAYISI** */}
                            <Form.Group className="mb-3">
                                <Form.Label>Hesaplanan Gün Sayısı</Form.Label>
                                <Form.Control
                                    type="text"
                                    readOnly
                                    value={calculatedDays > 0 ? `${calculatedDays} gün` : '-'}
                                    className="bg-light"
                                />
                            </Form.Group>

                            {/* **ONAYLAYACAK KİŞİ** */}
                            <Form.Group className="mb-3">
                                <Form.Label>Onaylayacak Kişi *</Form.Label>
                                <Form.Control
                                    type="text"
                                    required
                                    placeholder="İzni onaylayacak kişinin adını girin"
                                    value={formData.approverName}
                                    onChange={(e) => handleFormChange('approverName', e.target.value)}
                                />
                                <Form.Text className="text-muted">
                                    Bu kişi izin raporunda imza alanında görünecektir
                                </Form.Text>
                            </Form.Group>


                            {/* **SEBEP** */}
                            <Form.Group className="mb-3">
                                <Form.Label>Sebep</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    placeholder="İzin sebebinizi belirtiniz (opsiyonel)"
                                    value={formData.reason}
                                    onChange={(e) => handleFormChange('reason', e.target.value)}
                                />
                            </Form.Group>

                            {/* **WORKING DAYS PREVIEW** */}
                            {workingDaysPreview && (
                                <Alert variant="info" className="mt-2">
                                    <strong>📊 Detayli Hesaplama:</strong><br />
                                    📅 Toplam takvim gunu: {workingDaysPreview.totalDays}<br />
                                    💼 Dusulecek izin: {workingDaysPreview.workingDays} is gunu<br />
                                    🏖️ Hafta sonu/tatil: {workingDaysPreview.excludedDays} gun (dusulmez)
                                    {workingDaysPreview.breakdown?.holidayNames?.length > 0 && (
                                        <><br />🎉 Araya giren tatiller: {workingDaysPreview.breakdown.holidayNames.join(', ')}</>
                                    )}
                                </Alert>
                            )}

                            {/* **BİLGİLENDİRME** */}
                            <div className="p-2 bg-info bg-opacity-10 rounded">
                                <small className="text-info">
                                    ℹ️ İzin talebi admin tarafından oluşturulduğu için otomatik olarak onaylanacaktır.
                                </small>
                            </div>
                        </Col>
                    </Row>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={loading}>
                        İptal
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={loading || calculatedDays <= 0 || (formData.leaveType === 'yıllık' && calculatedDays > (employee?.leaveRights?.remaining || 0))}
                    >
                        {loading ? 'Oluşturuluyor...' : '📅 İzin Talebi Oluştur'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default EmployeeLeaveModal;